import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import {
    Button,
    VStack,
    HStack,
    Divider,
    Center,
    Text,
    Hide,
    useToast,
    useDisclosure,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppAlertDelete,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { InternshipScheduleList as ListComponent } from "./InternshipScheduleList";
import { InternshipScheduleForm as FormComponent } from "./InternshipScheduleForm";
import {
    InternshipSchedule as Entity,
    InternshipScheduleApi as EntityApi,
    Internship,
    InternshipApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";
import { useAuthData } from "../../../contexts";

export const InternshipSchedulePage = () => {
    // hook
    const { t } = useTrans();
    const { grant } = useAuthData();
    const toast = useToast();
    const navigate = useNavigate();
    const { id: internshipId } = useParams();
    const formContainerState = useFormContainerState();
    const deleteAlertState = useDisclosure();

    // state & const
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [actionSelectId, setActionSelectId] = useState(0);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("name");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
        };
        params[`order[${sortColumn}]`] = sortOrder;
        params["internship.id"] = internshipId;
        if (search) {
            params["internship_schedule_search"] = search;
        }

        await EntityApi.getCollection<Entity>(pageIndex + 1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "edit":
                formContainerState.open(data?.id);
                break;
            case "delete":
                setActionSelectId(data?.id);
                deleteAlertState.onOpen();
                break;
            case "internshipScheduleSeat":
                navigate(
                    cPath("ADMIN.INTERNSHIP_SCHEDULE_SEAT_PAGE", {
                        id: internshipId,
                        internshipScheduleId: data?.id,
                    })
                );
                break;
            case "internshipScheduleSkill":
                navigate(
                    cPath("ADMIN.INTERNSHIP_SCHEDULE_SKILL_PAGE", {
                        id: internshipId,
                        internshipScheduleId: data?.id,
                    })
                );
                break;
            case "students":
                navigate(
                    cPath("ADMIN.INTERNSHIP_SCHEDULE_STUDENT_PAGE", {
                        id: internshipId,
                        internshipScheduleId: data?.id,
                    })
                );
                break;
            case "timesheet":
                navigate(
                    cPath("ADMIN.INTERNSHIP_TIMESHEET_PAGE", {
                        id: internshipId,
                        internshipScheduleId: data?.id,
                    })
                );
                break;
            case "clone":
                cloneHandler(data?.id);
                break;
        }
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoadingList(true);
        await EntityApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchListData();
                }
            })
            .finally(() => setLoadingList(false));
    };

    const cloneHandler = async (internshipScheduleId: number) => {
        if (internshipScheduleId < 1) {
            return;
        }
        EntityApi.postScheduleClone<
            { success: boolean },
            { internshipScheduleId: number }
        >({
            internshipScheduleId,
        }).then(({ response, errorMessage }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                if (response.success) {
                    toast({
                        title: t(
                            "padm.InternshipSchedulePage:toast.success.clone"
                        ),
                        status: "success",
                    });
                    fetchListData();
                } else {
                    toast({
                        title: t(
                            "padm.InternshipSchedulePage:toast.fail.clone"
                        ),
                        status: "success",
                    });
                }
            }
        });
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        formContainerState.isOpen,
    ]);

    useEffect(() => {
        setLoading(true);
        if (internshipId) {
            InternshipApi.getItem<Internship>(internshipId)
                .then(({ errorMessage, isNotFound, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (response !== null) {
                        setInternship(response);
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [internshipId]);

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.InternshipSchedulePage:text.pageTitle")}
                breadcrumb={
                    internship?.name
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                          ]
                        : []
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    {(grant.isSchoolAndCompany ||
                        grant.isInternshipCoordinator ||
                        grant.isInternshipSupervisor) && (
                        <>
                            <Center height="2.1875rem">
                                <Divider
                                    orientation="vertical"
                                    borderColor={"var(--chakra-colors-greyT80)"}
                                />
                            </Center>
                            <Button
                                variant={"primary"}
                                onClick={() => formContainerState.open(0)}
                            >
                                <AppIcon name="ics-plus-circle" w="1rem" />
                                <Hide below="md">
                                    <Text ml={2}>
                                        {t(
                                            "padm.InternshipSchedulePage:button.create"
                                        )}
                                    </Text>
                                </Hide>
                            </Button>
                        </>
                    )}
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    <VStack p={6}>
                        <ListComponent
                            table={table}
                            paging={paging}
                            list={list}
                            loadingList={loadingList}
                            onActionClick={actionClickHandler}
                        />
                        {deleteAlertState.isOpen && (
                            <AppAlertDelete
                                isOpenDelete={deleteAlertState.isOpen}
                                deleteHandler={() => deleteHandler()}
                                onCloseDelete={deleteAlertState.onClose}
                            />
                        )}
                    </VStack>
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                            internshipId={internshipId || ""}
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
